import { useCallback, useEffect, useRef, useState } from 'react';
import { conversionScenarios } from '../data';

const currencyIcons: Record<string, string> = {
  USD: 'ri-money-dollar-circle-line',
  EUR: 'ri-money-euro-circle-line',
  GBP: 'ri-money-pound-circle-line',
};

const settleCurrency: Record<string, string> = {
  EURGBP: 'GBP',
  USDJPY: 'JPY',
  XAUUSD: 'USD',
};

const fmt = (n: number, max = 2) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: max });

type AnimPhase = 'idle' | 'fade-out' | 'replay';

export default function ConversionCostSection() {
  const [activeScenario, setActiveScenario] = useState(0);
  const [inView, setInView] = useState(false);
  const [animPhase, setAnimPhase] = useState<AnimPhase>('idle');
  const sectionRef = useRef<HTMLElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scenario = conversionScenarios[activeScenario];

  const perConversionCost = scenario.tradeSize * scenario.markup;
  const roundTripCost = perConversionCost * 2;
  const monthlyCost = roundTripCost * 20;
  const annualCost = monthlyCost * 12;
  const afterEntry = scenario.tradeSize - perConversionCost;
  const afterExit = afterEntry - perConversionCost;
  const leakPercent = (roundTripCost / scenario.tradeSize) * 100;

  const acct = scenario.baseCurrency;
  const settle = settleCurrency[scenario.tradePair] || 'USD';
  const markupPct = (scenario.markup * 100).toFixed(1);

  // Intersection observer
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.12 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Cleanup timer
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const switchScenario = useCallback((idx: number) => {
    if (idx === activeScenario) return;
    // 1) Fade everything out
    setAnimPhase('fade-out');
    if (timerRef.current) clearTimeout(timerRef.current);
    // 2) After vanish, swap values and start replay
    timerRef.current = setTimeout(() => {
      setActiveScenario(idx);
      setAnimPhase('replay');
      // 3) After replay animation completes, settle
      timerRef.current = setTimeout(() => {
        setAnimPhase('idle');
      }, 900);
    }, 280);
  }, [activeScenario]);

  // Whether the lane should show animations right now
  const showAnim = inView || animPhase !== 'idle';

  // Checkpoint visibility
  const cpVisible = animPhase === 'idle' ? inView : animPhase === 'replay';

  return (
    <section ref={sectionRef} className="w-full bg-background-100 py-12 md:py-18">
      <div className="max-w-main mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="max-w-[680px] mb-10 md:mb-14">
          <h2 className="text-[32px] md:text-[42px] font-[750] leading-[1.15] text-foreground-950 tracking-tight mb-4">
            The biggest hidden cost no one talks about
          </h2>
          <p className="text-[15px] leading-relaxed text-foreground-600">
            Every cross-currency trade crosses two conversion checkpoints — entry and exit. At each one, your broker skims a markup you never see on the ticket.
          </p>
        </div>

        {/* Scenario selector cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 mb-10 md:mb-12">
          {conversionScenarios.map((s, idx) => {
            const isActive = activeScenario === idx;
            return (
              <button
                key={s.label}
                onClick={() => switchScenario(idx)}
                className={`text-left rounded-xl p-4 md:p-5 border transition-all duration-200 cursor-pointer ${
                  isActive
                    ? 'bg-background-50 border-primary-400'
                    : 'bg-background-50/60 border-background-200 hover:border-primary-300'
                }`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                      isActive ? 'bg-primary-500' : 'bg-background-200'
                    }`}
                  >
                    <i
                      className={`${currencyIcons[s.baseCurrency] || 'ri-money-cny-circle-line'} text-[18px] ${
                        isActive ? 'text-background-50' : 'text-foreground-500'
                      }`}
                    />
                  </div>
                  <div>
                    <div className="text-[13px] font-semibold text-foreground-950">{s.baseCurrency} Account</div>
                    <div className="text-[12px] text-foreground-500">{s.tradePair}</div>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[12px] text-foreground-500">Trade size</span>
                  <span className="text-[13px] font-bold text-foreground-950">${fmt(s.tradeSize, 0)}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[12px] text-foreground-500">Markup</span>
                  <span className="text-[13px] font-bold text-accent-600">{(s.markup * 100).toFixed(1)}%</span>
                </div>
              </button>
            );
          })}
        </div>

        {/* ===== HERO: Conversion checkpoint lane ===== */}
        <div className="bg-background-50 rounded-card border border-background-200 p-5 md:p-10 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-6 md:mb-10">
            <div>
              <div className="text-[11px] font-bold text-accent-600 uppercase tracking-[0.14em] mb-1">
                Follow the money
              </div>
              <h3 className="text-[17px] md:text-[22px] font-bold text-foreground-950 transition-colors duration-300">
                One {acct} {fmt(scenario.tradeSize, 0)} trade, two border crossings
              </h3>
            </div>
            <div className="text-[12px] text-foreground-500 sm:text-right">
              {scenario.tradePair} settles in {settle} · {acct} ⇄ {settle} each way
            </div>
          </div>

          {/* The lane — desktop: horizontal; mobile: vertical flow */}
          <div className="flex flex-col lg:flex-row items-stretch gap-0">
            {/* Station 1: Capital enters */}
            <Station
              label="Your capital"
              currency={acct}
              currencyClass="text-primary-600"
              value={fmt(scenario.tradeSize, 0)}
              valueClass="text-foreground-950"
              subLabel="full value, untouched"
              animPhase={animPhase}
            />

            {/* Checkpoint 1 */}
            <Checkpoint
              index={1}
              from={acct}
              to={settle}
              markupPct={markupPct}
              deduction={perConversionCost}
              visible={cpVisible}
              delay={0}
            />

            {/* Station 2: In market */}
            <Station
              label="Live in market"
              currency={settle}
              currencyClass="text-foreground-500"
              value={fmt(afterEntry)}
              valueClass="text-foreground-700"
              subLabel={`already down $${fmt(perConversionCost)}`}
              animPhase={animPhase}
            />

            {/* Checkpoint 2 */}
            <Checkpoint
              index={2}
              from={settle}
              to={acct}
              markupPct={markupPct}
              deduction={perConversionCost}
              visible={cpVisible}
              delay={250}
            />

            {/* Station 3: Back in account */}
            <Station
              label="Back in account"
              currency={acct}
              currencyClass="text-primary-600"
              value={fmt(afterExit)}
              valueClass="text-foreground-950"
              subLabel={`−$${fmt(roundTripCost)} vanished`}
              subClass="text-accent-600 font-semibold"
              animPhase={animPhase}
            />
          </div>

          {/* Bottom summary strip */}
          <div className="mt-6 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 rounded-xl bg-background-100 px-4 md:px-5 py-3 md:py-4">
            <span className="text-[13px] text-foreground-600 text-center sm:text-left">
              Round-trip conversion cost on this single trade
            </span>
            <span className="text-[20px] font-[800] text-accent-600 tracking-tight tabular-nums transition-all duration-300">
              ${fmt(roundTripCost)}
            </span>
            <span className="text-[12px] text-foreground-400 text-center sm:text-right">
              ({leakPercent.toFixed(1)}% of capital — invisible on your ticket)
            </span>
          </div>
        </div>

        {/* ===== Escalation ladder ===== */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 bg-background-50 rounded-card border border-background-200 p-5 md:p-8">
            <div className="text-[11px] font-bold text-primary-600 uppercase tracking-[0.14em] mb-1">
              The compounding trap
            </div>
            <h3 className="text-[17px] md:text-[22px] font-bold text-foreground-950 mb-6 md:mb-8">
              One trade feels like nothing. A year doesn&apos;t.
            </h3>

            {/* Mobile: card list */}
            <div className="md:hidden space-y-3">
              <div className="rounded-xl bg-background-100 border border-background-200 p-4 flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wider mb-1">Per trade</div>
                  <div className="text-[22px] font-[800] text-foreground-950 tracking-tight tabular-nums">${fmt(roundTripCost, 0)}</div>
                </div>
                <div className="w-10 h-10 rounded-lg bg-secondary-400/20 flex items-center justify-center">
                  <i className="ri-exchange-dollar-line text-[18px] text-secondary-500" />
                </div>
              </div>

              <div className="flex items-center justify-center gap-2 py-1">
                <i className="ri-arrow-down-line text-[14px] text-foreground-300 w-4 h-4 flex items-center justify-center" />
                <span className="text-[11px] font-bold text-foreground-400">× 20 trades / month</span>
                <i className="ri-arrow-down-line text-[14px] text-foreground-300 w-4 h-4 flex items-center justify-center" />
              </div>

              <div className="rounded-xl bg-background-100 border border-background-200 p-4 flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wider mb-1">Per month</div>
                  <div className="text-[22px] font-[800] text-foreground-950 tracking-tight tabular-nums">${fmt(monthlyCost, 0)}</div>
                </div>
                <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                  <i className="ri-calendar-line text-[18px] text-primary-500" />
                </div>
              </div>

              <div className="flex items-center justify-center gap-2 py-1">
                <i className="ri-arrow-down-line text-[14px] text-foreground-300 w-4 h-4 flex items-center justify-center" />
                <span className="text-[11px] font-bold text-foreground-400">× 12 months</span>
                <i className="ri-arrow-down-line text-[14px] text-foreground-300 w-4 h-4 flex items-center justify-center" />
              </div>

              <div className="rounded-xl bg-accent-50 border border-accent-200 p-4 flex items-center justify-between">
                <div>
                  <div className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wider mb-1">Per year</div>
                  <div className="text-[26px] font-[800] text-accent-600 tracking-tight tabular-nums">${fmt(annualCost, 0)}</div>
                </div>
                <div className="w-10 h-10 rounded-lg bg-accent-500/20 flex items-center justify-center">
                  <i className="ri-fire-line text-[18px] text-accent-500" />
                </div>
              </div>
            </div>

            {/* Desktop: bar chart */}
            <div className="hidden md:flex flex-wrap items-end justify-center gap-x-3 gap-y-6 sm:flex-nowrap sm:justify-between md:gap-5">
              <LadderStep
                label="Per trade"
                value={fmt(roundTripCost, 0)}
                heightClass="h-16 md:h-20"
                barClass="bg-secondary-400"
                visible={showAnim}
                animPhase={animPhase}
                delay={200}
              />
              <Multiplier text="× 20 trades" visible={showAnim} animPhase={animPhase} delay={300} />
              <LadderStep
                label="Per month"
                value={fmt(monthlyCost, 0)}
                heightClass="h-28 md:h-36"
                barClass="bg-primary-500"
                visible={showAnim}
                animPhase={animPhase}
                delay={400}
              />
              <Multiplier text="× 12 months" visible={showAnim} animPhase={animPhase} delay={500} />
              <LadderStep
                label="Per year"
                value={fmt(annualCost, 0)}
                heightClass="h-44 md:h-56"
                barClass="bg-accent-500"
                highlight
                visible={showAnim}
                animPhase={animPhase}
                delay={600}
              />
            </div>
          </div>

          {/* Annual hero + how to reduce */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="bg-foreground-950 rounded-card p-5 md:p-8">
              <div className="text-[12px] font-semibold text-foreground-400 uppercase tracking-wider mb-3">
                Annual conversion drain
              </div>
              <div className="text-[40px] md:text-[58px] font-[800] text-accent-500 tracking-tight leading-none tabular-nums transition-all duration-500">
                ${fmt(annualCost, 0)}
              </div>
              <div className="text-[13px] text-foreground-400 mt-4 leading-relaxed">
                Gone before a single pip of spread, commission, or swap is counted — purely from crossing the {acct} ⇄ {settle} border twice per trade.
              </div>
            </div>

            <div className="bg-background-50 rounded-card border border-background-200 p-5 md:p-6">
              <div className="text-[12px] font-semibold text-foreground-500 uppercase tracking-wider mb-4">
                How to close the border
              </div>
              <ul className="space-y-3">
                {[
                  'Open a multi-currency account to skip conversions entirely',
                  'Trade pairs quoted in your account currency when possible',
                  'Ask for the exact conversion rate before confirming',
                  'Batch cross-currency trades to reduce crossings',
                ].map((tip) => (
                  <li key={tip} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-primary-100 flex items-center justify-center mt-0.5 shrink-0">
                      <i className="ri-check-line text-[12px] text-primary-600" />
                    </div>
                    <span className="text-[13px] text-foreground-700 leading-relaxed">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Station (capital / in market / back) ---------- */
function Station({
  label,
  currency,
  currencyClass,
  value,
  valueClass,
  subLabel,
  subClass = 'text-foreground-400',
  animPhase,
}: {
  label: string;
  currency: string;
  currencyClass: string;
  value: string;
  valueClass: string;
  subLabel: string;
  subClass?: string;
  animPhase: AnimPhase;
}) {
  return (
    <div className="flex-1 flex flex-col md:items-center text-center py-3 md:py-4">
      <div className="text-[11px] font-semibold text-foreground-500 uppercase tracking-wider mb-1 md:mb-2">
        {label}
      </div>
      <div className="flex items-baseline justify-center gap-1.5 md:flex-col md:gap-0">
        <div className={`text-[13px] font-bold md:mb-1 ${currencyClass}`}>{currency}</div>
        <div
          className={`text-[22px] md:text-[30px] font-[800] tracking-tight tabular-nums transition-all duration-300 ${valueClass}`}
        >
          {value}
        </div>
      </div>
      <div className={`text-[11px] mt-1 ${subClass}`}>{subLabel}</div>
    </div>
  );
}

/* ---------- Checkpoint (the conversion gate) ---------- */
function Checkpoint({
  index,
  from,
  to,
  markupPct,
  deduction,
  visible,
  delay,
}: {
  index: number;
  from: string;
  to: string;
  markupPct: string;
  deduction: number;
  visible: boolean;
  delay: number;
}) {
  return (
    <div className="flex lg:flex-col items-center justify-center gap-2 lg:gap-0 py-2 lg:py-0 lg:px-2 min-w-0 lg:w-[132px] shrink-0">
      {/* Dashed rail top */}
      <div className="hidden lg:block w-full border-t border-dashed border-background-300 mb-4" />

      <div
        className="relative flex flex-col items-center transition-all duration-[450ms] ease-out"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(-10px)',
          transitionDelay: visible ? `${delay}ms` : '0ms',
        }}
      >
        <div className="hidden md:block text-[10px] font-bold text-foreground-400 uppercase tracking-wider mb-1.5">
          Checkpoint {index}
        </div>
        <div className="flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1.5 mb-2">
          <span className="text-[12px] font-bold text-foreground-700">{from}</span>
          <i className="ri-arrow-right-line text-[13px] text-accent-500 w-3.5 h-3.5 flex items-center justify-center" />
          <span className="text-[12px] font-bold text-foreground-700">{to}</span>
        </div>
        <div className="rounded-lg bg-accent-50 border border-accent-200 px-3 py-2 text-center transition-all duration-300">
          <div className="text-[15px] font-[800] text-accent-600 tabular-nums leading-none">
            −${deduction.toFixed(2)}
          </div>
          <div className="text-[10px] font-semibold text-accent-500/90 mt-1">
            {markupPct}% skim
          </div>
        </div>
      </div>

      {/* Dashed rail bottom */}
      <div className="hidden lg:block w-full border-t border-dashed border-background-300 mt-4" />
    </div>
  );
}

/* ---------- Ladder step ---------- */
function LadderStep({
  label,
  value,
  heightClass,
  barClass,
  highlight = false,
  visible,
  animPhase,
  delay,
}: {
  label: string;
  value: string;
  heightClass: string;
  barClass: string;
  highlight?: boolean;
  visible: boolean;
  animPhase: AnimPhase;
  delay: number;
}) {
  // During fade-out, bar shrinks to 0. During replay, bar grows back.
  const barVisible = animPhase === 'fade-out' ? false : animPhase === 'replay' ? true : visible;

  return (
    <div className="flex-1 flex flex-col items-center justify-end">
      <div
        className={`text-[15px] md:text-[19px] font-[800] tracking-tight tabular-nums mb-2 transition-all duration-300 ${
          highlight ? 'text-accent-600' : 'text-foreground-950'
        }`}
      >
        ${value}
      </div>
      <div
        className={`w-full ${heightClass} ${barClass} rounded-t-lg origin-bottom transition-transform duration-[550ms] ease-out`}
        style={{
          transform: barVisible ? 'scaleY(1)' : 'scaleY(0)',
          transitionDelay: barVisible ? `${delay}ms` : '0ms',
        }}
      />
      <div className="text-[11px] md:text-[12px] font-semibold text-foreground-500 uppercase tracking-wider mt-3 whitespace-nowrap">
        {label}
      </div>
    </div>
  );
}

/* ---------- Multiplier connector ---------- */
function Multiplier({
  text,
  visible,
  animPhase,
  delay,
}: {
  text: string;
  visible: boolean;
  animPhase: AnimPhase;
  delay: number;
}) {
  const show = animPhase === 'fade-out' ? false : animPhase === 'replay' ? true : visible;

  return (
    <div
      className="flex flex-col items-center justify-end pb-8 md:pb-10 transition-opacity duration-[450ms]"
      style={{
        opacity: show ? 1 : 0,
        transitionDelay: show ? `${delay}ms` : '0ms',
      }}
    >
      <span className="text-[10px] md:text-[11px] font-bold text-foreground-400 uppercase tracking-wider whitespace-nowrap rotate-0">
        {text}
      </span>
      <i className="ri-arrow-right-line text-[16px] text-background-300 w-4 h-4 flex items-center justify-center mt-1" />
    </div>
  );
}